import { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

// Tamanho máximo aceito (espelha a validação do backend em Common/ImagemUpload.cs).
const TAMANHO_MAXIMO_BYTES = 2 * 1024 * 1024;
const TIPOS_ACEITOS = ['image/png', 'image/jpeg', 'image/webp'];

// Campo de upload de uma única imagem (logo do cliente / assinatura do usuário).
// `value` pode ser:
//   - um File (imagem recém-selecionada, ainda não enviada);
//   - uma string data URI ou URL (imagem já salva no backend);
//   - null / '' (sem imagem).
// `onChange(fileOrNull)` recebe o File selecionado ou null quando o usuário remove.
export default function ImageUploadField({
  label,
  value,
  onChange,
  hint = 'Opcional. PNG, JPEG ou WEBP até 2 MB. Prefira PNG com fundo transparente.',
  error,
  disabled = false,
  id,
}) {
  const inputRef = useRef(null);
  const [localError, setLocalError] = useState('');

  const isFile = value instanceof File;
  const objectUrl = useMemo(() => (isFile ? URL.createObjectURL(value) : null), [isFile, value]);

  useEffect(() => () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  const previewSrc = isFile ? objectUrl : (typeof value === 'string' && value ? value : null);

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    if (!TIPOS_ACEITOS.includes(file.type)) {
      setLocalError('Formato inválido. Envie uma imagem PNG, JPEG ou WEBP.');
      return;
    }
    if (file.size > TAMANHO_MAXIMO_BYTES) {
      setLocalError('A imagem deve ter no máximo 2 MB.');
      return;
    }

    setLocalError('');
    onChange(file);
  };

  const handleRemove = () => {
    setLocalError('');
    onChange(null);
  };

  const shownError = error || localError;

  return (
    <div className="space-y-2">
      {label ? (
        <span className="text-sm font-medium text-slate-700">{label}</span>
      ) : null}

      <div className="flex items-center gap-3">
        <div className="flex h-16 w-28 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-slate-50">
          {previewSrc ? (
            <img src={previewSrc} alt="Pré-visualização" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="px-2 text-center text-[10px] uppercase tracking-wide text-slate-400">Sem imagem</span>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current && inputRef.current.click()}
            className="w-fit rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {previewSrc ? 'Trocar imagem' : 'Selecionar imagem'}
          </button>
          {previewSrc ? (
            <button
              type="button"
              disabled={disabled}
              onClick={handleRemove}
              className="inline-flex w-fit items-center gap-1 text-xs font-medium text-rose-600 transition hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <X className="h-3 w-3" /> Remover
            </button>
          ) : null}
        </div>

        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={TIPOS_ACEITOS.join(',')}
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
      {shownError ? <p className="text-xs text-rose-600">{shownError}</p> : null}
    </div>
  );
}
